import { useState, useEffect, useCallback, type KeyboardEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RenameField } from '@/components/features/rename-field'
import { renameConversation, suggestConversationTitle } from '@/lib/api'
import { toast } from 'sonner'

interface ConversationRenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  conversationId: string
  currentTitle: string
  onRenamed: (newTitle: string) => void
}

export function ConversationRenameDialog({
  open,
  onOpenChange,
  conversationId,
  currentTitle,
  onRenamed,
}: ConversationRenameDialogProps) {
  const [title, setTitle] = useState(currentTitle)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [avoidTitles, setAvoidTitles] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setTitle(currentTitle)
      setAvoidTitles([])
    }
  }, [open, currentTitle])

  const handleSuggest = useCallback(async () => {
    setIsSuggesting(true)
    try {
      const response = await suggestConversationTitle(conversationId, {
        avoid: avoidTitles,
      })
      if (response.success && response.title) {
        setTitle(response.title)
        setAvoidTitles((prev) => [...prev, response.title].slice(-8))
      }
    } catch (error) {
      console.error('Failed to suggest conversation title:', error)
      toast.error('Failed to suggest title')
    } finally {
      setIsSuggesting(false)
    }
  }, [conversationId, avoidTitles])

  const handleSave = async () => {
    if (!title.trim() || title.trim() === currentTitle) {
      onOpenChange(false)
      return
    }

    setIsSaving(true)
    try {
      const response = await renameConversation(conversationId, title.trim())
      if (response.success) {
        onRenamed(response.conversation.title)
        onOpenChange(false)
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error)
      toast.error('Failed to rename conversation')
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSave()
    } else if (e.key === 'Escape') {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <RenameField
            value={title}
            onChange={setTitle}
            onSuggest={handleSuggest}
            isSuggesting={isSuggesting}
            suggestLabel="AI suggest title (click again to shuffle)"
            autoFocus
            disabled={isSaving}
            onKeyDown={handleKeyDown}
            inputClassName="font-sans"
          />
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSuggesting || isSaving}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isSaving || !title.trim()}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
